import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useAppSelector } from '../../../app/hooks';
import { selectUnavailableItemsByName } from '../../../selectors/menu';
import { selectModGroupPrompt } from '../../../redux/features/experiments/experiments.selector';
import {
  checkItemInCartItemTree,
  isDefaultItemInModGroup,
  isItem86edToday,
} from '../../../utils/menu';
import { useDisplayPreference } from '../../Layout/DisplayPreferenceContext';
import { getMenuUnificationFeatureFlag } from '../../../utils/session-storage';
import { isBMModifierAnUnavailableConstraint } from '../../../utils/menuUnification.utils';
import ModGroupRow, {
  MOD_GROUP_ROW_INDENT_PX,
} from '../components/ModGroupRow';
import ModOptionRow from '../components/ModOptionRow';
import OptionDrawer from '../components/OptionDrawer';
import { getCartItemDescription, sortAndFilterMenuItems } from '../util/helper';
import {
  buildModGroupRowModel,
  optionBringsFurtherGroups,
} from '../util/modGroupRow.utils';
import { HIDDEN_WHEN_CLOSED, useModGroupOpen } from './ModGroupOpenContext';
import RecursiveModGroup from './RecursiveModGroup';
import { IModGroup } from './modGroup.props';
import { canShowItem } from './modGroup.utils';

interface ModGroupRowViewProps extends IModGroup {
  isSingleChoice: boolean;
}

/**
 * HITL-1616 — the row-and-drawer rendering of one modifier group.
 *
 * One component for both the singular and multiple variants: the only thing
 * that differs between them is whether picking an option replaces the selection
 * or toggles it, and the count chip, which falls out of the shared row model.
 * Keeping the branch here rather than in both selection components is what stops
 * the two describing the same cart differently.
 *
 * ## What is NOT conditional
 *
 * The drawer and the nested groups are HIDDEN when closed, never unmounted.
 * `ModGroup.tsx:158-202` applies a group's default modifier selections from a
 * mount effect, and for a nested group revealed after add-time it is the only
 * mechanism that does — so unmounting a closed group would make the cart's
 * contents depend on which rows the agent opened, and in `Intercept` that
 * difference reaches the POS.
 *
 * ## What counts as open
 *
 * Everything, until the agent collapses it — the chip grid showed every
 * group's options at once and this keeps that. A collapsed group re-opens the
 * moment any of the three existing "go to that group" behaviours addresses it,
 * and those already arrive as props: `isSelectedToFocus` carries
 * `dialogSlice.selectedModGroupPath` (Shift+W/S/A/D and clicking a mod group in
 * the cart) and `isHighlighted` carries `dialogSlice.highlightedModifier` (the
 * omnibar's modifier search). So all of them open the drawer by construction
 * rather than scrolling to a closed row.
 */
const ModGroupRowView = (props: ModGroupRowViewProps) => {
  const {
    modGroup,
    cartItem,
    pathToItem,
    parentModifier,
    isSelectedToFocus,
    isHighlighted,
    isSingleChoice,
    selectMenuItemModifier,
    sendModGroupTTSMessage,
    sendTTSMessage,
  } = props;

  const unavailableItemsByName = useSelector(selectUnavailableItemsByName);
  const { isClosedByAgent, toggle, reopen } = useModGroupOpen();
  // HITL-1696: the agent's centre-pane look, read once per group and handed
  // down, so the rows stay presentational. Defaults (rows, menu case) leave
  // every row exactly as HITL-1616 drew it.
  const { pref: displayPref } = useDisplayPreference();
  const boxed = displayPref.cpStyle === 'boxes';
  const caps = displayPref.cpCase === 'caps';
  const optionFontSize = displayPref.cpFontSize;
  const isBrandMenu = getMenuUnificationFeatureFlag();

  const model = buildModGroupRowModel(modGroup, cartItem, pathToItem);
  // Open unless the agent collapsed it. Nothing else is OR-ed in here: the
  // outside signals below are transitions that CLEAR a collapse, not conditions
  // that outrank it — `isSelectedToFocus` stays true for as long as the group is
  // selected, so reading it here made the selected group impossible to collapse.
  const isOpen = !isClosedByAgent(pathToItem);

  // Navigating to a group reveals it, even if the agent had shut it earlier.
  // Fires on the transition, so a collapse afterwards sticks.
  useEffect(() => {
    if (isSelectedToFocus || isHighlighted) reopen(pathToItem);
  }, [isSelectedToFocus, isHighlighted, pathToItem, reopen]);

  // Depth from the path: a top-level group is one segment, each nesting level
  // adds an option id and a group id.
  const depth = Math.max(0, Math.floor(pathToItem.split('__').length / 2));

  // What the info button speaks for this slot; undefined while the car withholds the group.
  const spokenDescription = useAppSelector((state) =>
    selectModGroupPrompt(
      state,
      {
        cartItemId: cartItem.cartItemId,
        pathToItem,
        constraintName: modGroup.name,
      },
      modGroup.description
    )
  );

  // Same condition the legacy heading used (`SingularSelectionModGroup:113`):
  // brand menu only, and only when the group actually carries a description.
  const hasSpokenDescription = !!(
    isBrandMenu &&
    modGroup.description &&
    spokenDescription
  );

  const menuItems = isBrandMenu
    ? Object.values(modGroup.menuItems)
    : sortAndFilterMenuItems(modGroup.menuItems);

  const options = menuItems.map((child) => {
    const is86edToday = isItem86edToday(child);
    const pathToModChoice = [pathToItem, child.id].join('__');
    const isSelected = !!checkItemInCartItemTree({
      cartItem,
      pathToItem: pathToModChoice,
    });

    const isItemUnavailableNow = isBrandMenu
      ? isBMModifierAnUnavailableConstraint(
          cartItem,
          unavailableItemsByName,
          parentModifier,
          pathToItem,
          modGroup.name,
          child.name
        )
      : false;

    const showItem = canShowItem({
      isBrandMenu,
      unavailableItemsByName,
      itemName: child.name,
      cartItem,
      modGroupName: modGroup.name,
      childAvailability: child?.available,
      is86edToday,
    });
    if (!showItem) return null;

    const childDescription = getCartItemDescription(child, isBrandMenu);

    return (
      <ModOptionRow
        key={pathToModChoice}
        name={child.name}
        description={childDescription}
        onInfoClick={
          childDescription ? () => sendTTSMessage(childDescription) : undefined
        }
        isSelected={isSelected}
        is86ed={
          isBrandMenu
            ? !child.available || is86edToday || isItemUnavailableNow
            : is86edToday
        }
        bringsFurtherGroups={optionBringsFurtherGroups(child)}
        boxed={boxed}
        // The same test the chip used for its dashed border
        // (`SingularSelectionModGroup`), keyed on the menu item id.
        isDefault={boxed && isDefaultItemInModGroup(child.itemId, modGroup)}
        caps={caps}
        fontSize={optionFontSize}
        // A single-choice group replaces its selection, so picking an already
        // selected option is a no-op rather than a deselect; a multi-select
        // group toggles. Same rule `handleModChange` applies to the radio and
        // checkbox events it replaces.
        onSelect={() =>
          selectMenuItemModifier(
            child,
            modGroup,
            pathToItem,
            isSingleChoice ? true : !isSelected,
            // `fromClick` — a human did this. It is what makes
            // `CartItemDetail` send the `agent_interception` that tells the AI
            // the order changed and marks the first interception for the POS.
            // The radio and checkbox handler this row replaces passes it
            // (ModGroup.tsx:214); omitting it left the cart changing with
            // nobody outside the terminal informed.
            true,
            // ...and NOT an automatic default, which is the other half of the
            // gate in `onMenuItemModifierSelect`.
            false
          )
        }
      />
    );
  });

  return (
    <div data-testid="mod-group-row-view">
      <ModGroupRow
        model={model}
        isOpen={isOpen}
        onToggle={() => toggle(pathToItem)}
        depth={depth}
        parentModifier={parentModifier}
        caps={caps}
        description={spokenDescription}
        onInfoClick={
          hasSpokenDescription
            ? () =>
                sendModGroupTTSMessage(modGroup, {
                  cartItemId: cartItem.cartItemId,
                  pathToItem,
                })
            : undefined
        }
      />
      <div style={isOpen ? undefined : HIDDEN_WHEN_CLOSED}>
        <OptionDrawer indentPx={depth * MOD_GROUP_ROW_INDENT_PX} boxed={boxed}>
          {options}
        </OptionDrawer>
        <RecursiveModGroup {...props} />
      </div>
    </div>
  );
};

export default ModGroupRowView;
